import { useEffect, useRef, useState, useMemo } from "react";
import { Crosshair, Search, Loader2, MapPin } from "lucide-react";
import {
  mapboxgl,
  MAP_STYLE,
  NEON,
  INK,
  MAPBOX_TOKEN,
  reverseGeocode,
  forwardGeocode,
} from "@/lib/mapbox";

const DEFAULT_CENTER = [78.9629, 20.5937]; // [lng, lat] — India centroid
const DEFAULT_ZOOM = 4.2;
const PICKED_ZOOM = 14.5;

/**
 * LocationPicker — premium Mapbox picker.
 * value: { latitude, longitude, exact_address, rough_area }
 */
export default function LocationPicker({
  value,
  onChange,
  testidPrefix = "loc",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef();

  const hasPick = value?.latitude != null && value?.longitude != null;
  const currentCenter = useMemo(
    () => (hasPick ? [value.longitude, value.latitude] : DEFAULT_CENTER),
    [hasPick, value?.longitude, value?.latitude]
  );

  // ---- init map once ----
  useEffect(() => {
    if (!containerRef.current || !MAPBOX_TOKEN) return;
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: currentCenter,
      zoom: hasPick ? PICKED_ZOOM : DEFAULT_ZOOM,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");

    // 🔧 Defensive canvas resize — see MapPage.jsx for full rationale.
    const safeResize = () => { try { map.resize(); } catch { /* noop */ } };
    requestAnimationFrame(safeResize);
    const t1 = setTimeout(safeResize, 100);
    const t2 = setTimeout(safeResize, 500);
    map.on("style.load", safeResize);
    map.on("load", safeResize);
    const ro = new ResizeObserver(safeResize);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", safeResize);

    map.on("load", () => {
      setReady(true);
    });
    map.on("click", (e) => {
      pick(e.lngLat.lng, e.lngLat.lat, true);
    });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      try { ro.disconnect(); } catch { /* noop */ }
      window.removeEventListener("resize", safeResize);
      try { map.remove(); } catch { /* noop */ }
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- sync marker with value ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    if (!hasPick) {
      if (markerRef.current) {
        try { markerRef.current.remove(); } catch { /* noop */ }
        markerRef.current = null;
      }
      return;
    }
    const lngLat = [value.longitude, value.latitude];
    if (!markerRef.current) {
      const el = document.createElement("div");
      el.className = "inonit-pick-marker";
      el.innerHTML = `<span class="dot"></span><span class="halo"></span>`;
      markerRef.current = new mapboxgl.Marker({
        element: el,
        anchor: "bottom",
        draggable: true,
      })
        .setLngLat(lngLat)
        .addTo(map);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLngLat();
        pick(p.lng, p.lat, true);
      });
    } else {
      markerRef.current.setLngLat(lngLat);
    }
    map.flyTo({ center: lngLat, zoom: PICKED_ZOOM, duration: 800, essential: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPick, value?.latitude, value?.longitude, ready]);

  const pick = async (lng, lat, doReverse) => {
    onChange({
      ...(value || {}),
      latitude: lat,
      longitude: lng,
    });
    if (doReverse) {
      setResolving(true);
      const g = await reverseGeocode(lng, lat);
      setResolving(false);
      if (g) {
        onChange({
          latitude: lat,
          longitude: lng,
          exact_address: g.exact || value?.exact_address || "",
          rough_area: g.area || g.city || value?.rough_area || "",
          // City is also surfaced so CreateEventPage can auto-fill the
          // City input — the consumer decides whether to use it.
          city: g.city || null,
        });
      }
    }
  };

  // ---- forward search (debounced) ----
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!searchInput || searchInput.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const proximity = hasPick ? [value.longitude, value.latitude] : null;
      const r = await forwardGeocode(searchInput, { proximity, limit: 6 });
      setResults(r);
      setSearching(false);
      setShowResults(true);
    }, 320);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const onResultPick = (r) => {
    setResults([]);
    setShowResults(false);
    setSearchInput(r.label);
    pick(r.lng, r.lat, true);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setResolving(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        pick(pos.coords.longitude, pos.coords.latitude, true);
        setResolving(false);
      },
      () => setResolving(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div className="border-[3px] border-ink rounded-2xl p-6 text-ink/55 text-sm bg-cream">
        Map unavailable — Mapbox token missing in env.
      </div>
    );
  }

  return (
    <div data-testid={`${testidPrefix}-picker`} className="flex flex-col gap-3">
      {/* Search bar */}
      <div className="relative">
        <Search size={16} strokeWidth={2.2} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/45 z-10" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 180)}
          placeholder="Search a place — café, neighbourhood, address…"
          data-testid={`${testidPrefix}-search`}
          className="chunky-input rounded-full pl-12 pr-12"
        />
        {searching && (
          <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/50 animate-spin" />
        )}
        {showResults && results.length > 0 && (
          <div
            data-testid={`${testidPrefix}-search-results`}
            className="absolute z-20 left-0 right-0 mt-2 border-[2.5px] border-ink rounded-2xl bg-paper max-h-72 overflow-y-auto shadow-pop"
          >
            {results.map((r, i) => (
              <button
                key={`${r.lng}-${r.lat}-${i}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onResultPick(r)}
                data-testid={`${testidPrefix}-search-result-${i}`}
                className="flex items-start gap-3 w-full text-left px-4 py-3 text-sm text-ink hover:bg-lime/40 border-b-2 border-ink/10 last:border-b-0 transition-colors"
              >
                <MapPin size={14} strokeWidth={2} className="text-ink mt-0.5 shrink-0" />
                <span className="leading-snug">{r.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={resolving}
          data-testid={`${testidPrefix}-use-current`}
          className="inline-flex items-center gap-2 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime text-ink px-4 py-2 text-xs font-extrabold uppercase tracking-widest shadow-pop-sm active:translate-x-[1px] active:translate-y-[1px] transition-[transform,background-color] disabled:opacity-60"
        >
          {resolving ? <Loader2 size={12} className="animate-spin" /> : <Crosshair size={12} strokeWidth={2.5} />}
          Use my location
        </button>
        <span className="text-xs text-ink/55">or tap the map to drop a pin</span>
      </div>

      {/* Map */}
      <div className="relative border-[3px] border-ink rounded-[1.5rem] h-72 sm:h-80 overflow-hidden bg-cream">
        <div
          ref={containerRef}
          data-testid={`${testidPrefix}-map`}
          className={`w-full h-full transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0"}`}
        />
        {!ready && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[linear-gradient(110deg,rgba(10,10,10,0.03),rgba(10,10,10,0.07),rgba(10,10,10,0.03))] bg-[length:200%_100%] animate-shimmer-picker" />
          </div>
        )}
      </div>

      {/* Address fields — auto-filled by reverse geocode */}
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          type="text"
          value={value?.exact_address || ""}
          onChange={(e) => onChange({ ...(value || {}), exact_address: e.target.value })}
          placeholder="Exact address (auto-filled)"
          data-testid={`${testidPrefix}-exact`}
          className="chunky-input"
        />
        <input
          type="text"
          value={value?.rough_area || ""}
          onChange={(e) => onChange({ ...(value || {}), rough_area: e.target.value })}
          placeholder="Rough area shown publicly"
          data-testid={`${testidPrefix}-rough`}
          className="chunky-input"
        />
      </div>

      <p className="text-[11px] text-ink/45">
        Public visitors only see the rough area. Approved attendees see the exact pin + address.
      </p>

      <style>{`
        /* Picker pin — animate the inner .dot, not the marker root
           (Mapbox positions the root via transform: translate3d(...)) */
        .inonit-pick-marker {
          position: relative;
          width: 32px; height: 32px;
          cursor: grab;
        }
        .inonit-pick-marker:active { cursor: grabbing; }
        .inonit-pick-marker .dot {
          position: absolute; left: 50%; top: 50%;
          width: 24px; height: 24px;
          margin-left: -12px; margin-top: -12px;
          background: ${NEON};
          border: 3px solid ${INK};
          border-radius: 9999px;
          box-shadow: 0 3px 0 0 ${INK};
          z-index: 2;
          animation: pin-drop-pick 520ms cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .inonit-pick-marker .halo {
          position: absolute; left: 50%; top: 50%;
          width: 24px; height: 24px;
          margin-left: -12px; margin-top: -12px;
          border-radius: 9999px;
          background: ${NEON};
          opacity: 0.4;
          animation: gentle-pulse-pick 2.4s cubic-bezier(0.22,0.61,0.36,1) infinite;
          z-index: 1;
        }
        @keyframes pin-drop-pick {
          0%   { transform: translateY(-22px) scale(0.7); opacity: 0; }
          60%  { transform: translateY(2px)  scale(1.06); opacity: 1; }
          100% { transform: translateY(0)    scale(1);    opacity: 1; }
        }
        @keyframes gentle-pulse-pick {
          0%   { transform: scale(1);   opacity: 0.4; }
          80%  { transform: scale(2.3); opacity: 0; }
          100% { transform: scale(2.3); opacity: 0; }
        }
        @keyframes shimmer-picker {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-shimmer-picker { animation: shimmer-picker 1.6s linear infinite; }

        /* Editorial control polish — picker map controls (large tap targets) */
        .inonit-pick-marker ~ * .mapboxgl-ctrl-group,
        .mapboxgl-ctrl-group {
          background: #FCFAF1 !important;
          box-shadow: 3px 3px 0 0 ${INK} !important;
          border: 2.5px solid ${INK} !important;
          border-radius: 1rem !important;
          overflow: hidden;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
          width: 42px !important;
          height: 42px !important;
        }
        .mapboxgl-ctrl-group button + button { border-top: 2px solid ${INK} !important; }
        .mapboxgl-ctrl-group button:hover { background-color: ${NEON} !important; }
        .mapboxgl-ctrl-attrib {
          background: rgba(244,240,225,0.85) !important;
          color: rgba(10,10,10,0.55) !important;
        }
        .mapboxgl-ctrl-attrib a { color: ${INK} !important; }
      `}</style>
    </div>
  );
}
